import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatCurrency, formatDate } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CheckCircle, XCircle, AlertCircle, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function PublicDocumentViewPage() {
  const { token } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseType, setResponseType] = useState(null); // 'accept' or 'decline'
  const [responseName, setResponseName] = useState('');
  const [responseComment, setResponseComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDocument();
  }, [token]);

  const fetchDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/api/public/view/${token}`);
      
      if (response.status === 404) {
        setError({ type: 'not_found', message: 'Document not found or link is invalid.' });
        return;
      }
      
      if (response.status === 410) {
        setError({ type: 'expired', message: 'This link has expired or been revoked.' });
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const data = await response.json();
      setDocument(data);
    } catch (err) {
      setError({ type: 'error', message: 'Failed to load document. Please try again.' });
      console.error('Error fetching document:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async () => {
    if (!responseType) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/public/view/${token}/${responseType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response_name: responseName || null,
          response_comment: responseComment || null,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit response');
      }
      
      const result = await response.json();
      toast.success(result.message);
      
      // Refresh document to show updated state
      await fetchDocument();
      setShowResponseModal(false);
      setResponseName('');
      setResponseComment('');
      setResponseType(null);
    } catch (err) {
      toast.error(err.message);
      console.error('Error submitting response:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openResponseModal = (type) => {
    setResponseType(type);
    setShowResponseModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="public-doc-loading">
        <div className="text-center">
          <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4 animate-spin" />
          <p className="text-slate-500">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="public-doc-error">
        <Card className="max-w-md w-full border border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-rose-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {error.type === 'not_found' ? 'Document Not Found' : error.type === 'expired' ? 'Link Expired' : 'Error'}
            </h1>
            <p className="text-slate-600">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Document loaded
  const isQuote = document.document_type === 'quote';
  const isEstimate = document.document_type === 'estimate';
  const isInvoice = document.document_type === 'invoice';
  const isCreditNote = document.document_type === 'credit_note';
  const canRespond = document.can_respond && !document.has_responded;
  const hasResponded = document.has_responded;
  const taxExemptionReasons = [...new Set(
    (document.line_items || []).map((item) => item.tax_exemption_reason).filter(Boolean)
  )];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-doc-view">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {document.company_logo_url && (
                  <img
                    src={document.company_logo_url}
                    alt={document.company_name}
                    className="max-h-12 max-w-[160px] object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div>
                  <CardTitle className="text-2xl font-bold text-slate-900">{document.company_name}</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {isQuote && 'Quote'}
                    {isEstimate && 'Estimate'}
                    {isInvoice && 'Invoice'}
                    {isCreditNote && 'Credit Note'}
                    {' '}
                    #{document.document_number}
                  </p>
                </div>
              </div>
              <Badge className={`${
                document.status === 'approved' || document.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                document.status === 'rejected' || document.status === 'void' ? 'bg-rose-100 text-rose-800' :
                'bg-amber-100 text-amber-800'
              }`}>
                {document.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-y-4">
              <div>
                <p className="text-xs text-slate-500 font-medium">Client</p>
                <p className="text-sm text-slate-900">{document.client_name}</p>
              </div>
              {document.project_name && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Project</p>
                  <p className="text-sm text-slate-900">{document.project_name}</p>
                </div>
              )}
              {isQuote && document.quote_date && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Quote Date</p>
                  <p className="text-sm text-slate-900">{formatDate(document.quote_date)}</p>
                </div>
              )}
              {isEstimate && document.estimate_date && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Estimate Date</p>
                  <p className="text-sm text-slate-900">{formatDate(document.estimate_date)}</p>
                </div>
              )}
              {isInvoice && document.invoice_date && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Invoice Date</p>
                  <p className="text-sm text-slate-900">{formatDate(document.invoice_date)}</p>
                </div>
              )}
              {isInvoice && document.due_date && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Due Date</p>
                  <p className="text-sm text-slate-900">{formatDate(document.due_date)}</p>
                </div>
              )}
              {isCreditNote && document.issue_date && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Issue Date</p>
                  <p className="text-sm text-slate-900">{formatDate(document.issue_date)}</p>
                </div>
              )}
              {isCreditNote && document.reason && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Reason</p>
                  <p className="text-sm text-slate-900">{document.reason}</p>
                </div>
              )}
              {document.client_po_number && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">PO Number</p>
                  <p className="text-sm text-slate-900">{document.client_po_number}</p>
                </div>
              )}
              {document.client_ticket_number && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Ticket Number</p>
                  <p className="text-sm text-slate-900">{document.client_ticket_number}</p>
                </div>
              )}
            </div>
            {document.title && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 font-medium">Title</p>
                <p className="text-sm text-slate-900">{document.title}</p>
              </div>
            )}
            {document.description && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 font-medium">Description</p>
                <p className="text-sm text-slate-900">{document.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Status Banner */}
        {hasResponded && (
          <Alert className={`${
            document.response_action === 'accepted' ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
          }`} data-testid="response-status-banner">
            {document.response_action === 'accepted' ? (
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            ) : (
              <XCircle className="w-5 h-5 text-rose-600" />
            )}
            <AlertDescription className={`${
              document.response_action === 'accepted' ? 'text-emerald-800' : 'text-rose-800'
            } font-medium`}>
              You have {document.response_action === 'accepted' ? 'accepted' : 'declined'} this {document.document_type}.
            </AlertDescription>
          </Alert>
        )}

        {/* Line Items */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Line Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  {isEstimate && <TableHead className="text-right">Type</TableHead>}
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  {isCreditNote ? (
                    <TableHead className="text-right">Tax Rate</TableHead>
                  ) : (
                    <TableHead className="text-right">Service Date</TableHead>
                  )}
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {document.line_items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    {isEstimate && <TableCell className="text-right text-sm text-slate-500">{item.item_type || 'N/A'}</TableCell>}
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                    {isCreditNote ? (
                      <TableCell className="text-right text-sm text-slate-500">
                        {(Number(item.tax_rate) * 100).toFixed(2)}%
                      </TableCell>
                    ) : (
                      <TableCell className="text-right text-sm text-slate-500">
                        {item.service_date ? formatDate(item.service_date) : '-'}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-medium">
                      {formatCurrency(isCreditNote ? item.line_total : item.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="space-y-2">
              {isCreditNote ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium text-slate-900">{formatCurrency(document.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Tax</span>
                    <span className="font-medium text-slate-900">{formatCurrency(document.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2 mt-2">
                    <span className="text-slate-900">Total Credit Amount</span>
                    <span className="text-slate-900">{formatCurrency(document.total_amount)}</span>
                  </div>
                  {document.amount_applied > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Amount Applied</span>
                      <span className="font-medium text-slate-900">{formatCurrency(document.amount_applied)}</span>
                    </div>
                  )}
                  {document.amount_remaining > 0 && (
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-slate-600">Available Credit Remaining</span>
                      <span className="text-slate-900">{formatCurrency(document.amount_remaining)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
              {isEstimate && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Labor Subtotal</span>
                    <span className="font-medium text-slate-900">{formatCurrency(document.labor_subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Materials Subtotal</span>
                    <span className="font-medium text-slate-900">{formatCurrency(document.materials_subtotal)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium text-slate-900">{formatCurrency(document.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{document.primary_tax_label || 'GST'}</span>
                <span className="font-medium text-slate-900">{formatCurrency(document.gst_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{document.secondary_tax_label || 'PST'}</span>
                <span className="font-medium text-slate-900">{formatCurrency(document.pst_amount)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2 mt-2">
                <span className="text-slate-900">Total</span>
                <span className="text-slate-900">{formatCurrency(document.total)}</span>
              </div>
              {isInvoice && (
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-slate-900">Balance Due</span>
                  <span className="text-emerald-600">{formatCurrency(document.balance_due)}</span>
                </div>
              )}
                </>
              )}
            </div>
            {taxExemptionReasons.length > 0 && (
              <div className="mt-4 border-t border-slate-200 pt-3" data-testid="tax-exemption-note">
                <p className="text-xs font-medium text-slate-500 mb-1">Tax treatment / Steuerliche Behandlung</p>
                {taxExemptionReasons.map((reason) => (
                  <p key={reason} className="text-sm text-slate-700">{reason}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {isCreditNote && document.applications?.length > 0 && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Applied to Invoices</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice Number</TableHead>
                    <TableHead className="text-right">Amount Applied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {document.applications.map((app, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{app.invoice_number}</TableCell>
                      <TableCell className="text-right">{formatCurrency(app.amount_applied)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Notes/Terms */}
        {(document.notes || document.terms || document.payment_terms) && (
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6 space-y-4">
              {document.notes && (
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Notes</p>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap">{document.notes}</p>
                </div>
              )}
              {document.terms && (
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Terms</p>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap">{document.terms}</p>
                </div>
              )}
              {document.payment_terms && (
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Payment Terms</p>
                  <p className="text-sm text-slate-900">{document.payment_terms}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons (for quotes/estimates only) */}
        {canRespond && (
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm text-slate-600 mb-4">Please review this {document.document_type} and let us know if you would like to proceed.</p>
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => openResponseModal('accept')}
                  data-testid="accept-button"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept {isQuote ? 'Quote' : 'Estimate'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => openResponseModal('decline')}
                  data-testid="decline-button"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 py-4">
          <FileText className="w-4 h-4 mx-auto mb-1" />
          <p>Powered by Mayr Technologies</p>
        </div>
      </div>

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {responseType === 'accept' ? 'Accept' : 'Decline'} {isQuote ? 'Quote' : 'Estimate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {responseType === 'accept'
                ? `You are about to accept this ${document.document_type}. This will notify the service provider.`
                : `You are about to decline this ${document.document_type}. Please provide a reason if possible.`}
            </p>
            <div className="space-y-2">
              <Label>Your Name (optional)</Label>
              <Input
                value={responseName}
                onChange={(e) => setResponseName(e.target.value)}
                placeholder="John Doe"
                data-testid="response-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Comment (optional)</Label>
              <Textarea
                value={responseComment}
                onChange={(e) => setResponseComment(e.target.value)}
                placeholder="Add any comments or notes..."
                rows={3}
                data-testid="response-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              className={responseType === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              onClick={handleResponse}
              disabled={submitting}
              data-testid="confirm-response-button"
            >
              {submitting ? 'Submitting...' : `Confirm ${responseType === 'accept' ? 'Accept' : 'Decline'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
